/**
 * Integer minor-unit money helpers.
 * Inputs: ISO currency + integer minor units. Outputs: exact sums, no float math.
 * curl examples are not applicable; used by ledger and API validation.
 */

export type Money = {
  currency: string;
  amountMinor: number;
};

const ZERO_DECIMAL = new Set(["JPY", "KRW", "VND"]);

export function money(currency: string, amountMinor: number): Money {
  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new Error("Currency must be a 3-letter ISO code");
  }
  if (!Number.isInteger(amountMinor)) {
    throw new Error("amountMinor must be an integer");
  }
  return { currency, amountMinor };
}

export function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { currency: a.currency, amountMinor: a.amountMinor + b.amountMinor };
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { currency: a.currency, amountMinor: a.amountMinor - b.amountMinor };
}

export function sumMoney(items: Money[], currency: string): Money {
  return items.reduce((acc, item) => addMoney(acc, item), money(currency, 0));
}

export function minorFactor(currency: string): number {
  return ZERO_DECIMAL.has(currency) ? 1 : 100;
}

export function formatMoney(value: Money, locale = "en-GB"): string {
  const factor = minorFactor(value.currency);
  const major = value.amountMinor / factor;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: value.currency,
    minimumFractionDigits: factor === 1 ? 0 : 2,
    maximumFractionDigits: factor === 1 ? 0 : 2,
  }).format(major);
}

/**
 * Split a total across named shares so residuals stay with the last share.
 * Percentages are integer basis points (10000 = 100%).
 */
export function allocateByBasisPoints(
  total: Money,
  shares: { id: string; basisPoints: number }[],
): { id: string; amount: Money }[] {
  const totalBp = shares.reduce((sum, share) => sum + share.basisPoints, 0);
  if (totalBp !== 10_000) {
    throw new Error("Basis points must sum to 10000");
  }
  let allocated = 0;
  return shares.map((share, index) => {
    const amountMinor =
      index === shares.length - 1
        ? total.amountMinor - allocated
        : Math.trunc((total.amountMinor * share.basisPoints) / 10_000);
    allocated += amountMinor;
    return { id: share.id, amount: money(total.currency, amountMinor) };
  });
}

export function allocationsSumToTotal(
  total: Money,
  allocations: { amountMinor: number }[],
): boolean {
  const sum = allocations.reduce((acc, row) => acc + row.amountMinor, 0);
  return sum === total.amountMinor;
}

export function groupMinorByCurrency(
  rows: { currency: string; amountMinor: number }[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    out[row.currency] = (out[row.currency] ?? 0) + row.amountMinor;
  }
  return out;
}

export function formatMinor(currency: string, amountMinor: number, locale = "en-GB"): string {
  return formatMoney(money(currency, amountMinor), locale);
}
